import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { useAllTopics } from "./useAllTopics.ts";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { useToCreateTopicScreen } from "./useToCreateTopicScreen.ts";
import { TopicTable } from "./TopicTable.tsx";
import { useToEditTopicScreen } from "./useToEditTopicScreen.ts";
import { Card } from "../UI/Card.tsx";
import { useLogicallyDeleteTopic } from "./useLogicallyDeleteTopic.ts";
import { GlobalSpinner } from "../UI/Loading/GlobalSpinner.tsx";
import { TopicItemFragment } from "../gql/graphql.ts";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { DebouncedTextInput } from "../UI/DebouncedTextInput.tsx";
import { useTopicTableUrlState } from "./useTopicTableUrlState.ts";
import {
  matchesTopicTableFilters,
  TopicTableSort,
  TopicTableSortField,
} from "./topicTableState.ts";

const SORT_FIELD_LABELS: Record<TopicTableSortField, string> = {
  name: "name",
  level: "level",
  pdfUrl: "PDF url",
  userGroups: "user groups",
};

function SortedByNotice({
  sort,
  onReset,
}: {
  sort: TopicTableSort | undefined;
  onReset: () => void;
}) {
  if (!sort) return null;
  return (
    <span className={"text-sm text-slate-500"}>
      Sorted by {SORT_FIELD_LABELS[sort.sortBy]} (
      {sort.sortDirection === "asc" ? "ascending" : "descending"}) ·{" "}
      <button
        onClick={onReset}
        className={"text-slate-600 underline hover:text-slate-900"}
      >
        Reset sorting
      </button>
    </span>
  );
}

function userGroupOptionsOf(topics: TopicItemFragment[]) {
  const nameById = new Map(
    topics.flatMap((topic) =>
      topic.userGroups.map((userGroup) => [userGroup.id, userGroup.name]),
    ),
  );
  return Array.from(nameById, ([value, label]) => ({ value, label })).sort(
    (a, b) => a.label.localeCompare(b.label),
  );
}

export const AllTopicScreenPath = "/topics";
export function TopicScreen() {
  const { topics, loading } = useAllTopics();
  const { sort, filters, setSort, setSearch, setColumnFilters } =
    useTopicTableUrlState();

  const visibleTopics = useMemo(
    () => topics.filter((topic) => matchesTopicTableFilters(topic, filters)),
    [topics, filters],
  );
  const userGroupOptions = useMemo(() => userGroupOptionsOf(topics), [topics]);
  const { toCreateTopic } = useToCreateTopicScreen();
  const { toEditTopic } = useToEditTopicScreen();
  const { logicallyDeleteTopic, loading: deletingTopic } =
    useLogicallyDeleteTopic();
  const askConfirmation = useConfirm();

  async function deleteTopic(topic: TopicItemFragment) {
    if (
      await askConfirmation({
        title: `Delete "${topic.name}"?`,
        message: `Level ${topic.level}`,
        confirmLabel: "Delete",
        isDestructive: true,
      })
    ) {
      await logicallyDeleteTopic(topic.id);
    }
  }

  return (
    <>
      <GlobalSpinner loading={deletingTopic} message={"Deleting topic"} />
      <Layout>
        <Card>
          <ScreenTitleBlock
            title={"Topics"}
            ctaOnClick={() => toCreateTopic()}
            ctaLabel={"New Topic"}
            ctaIcon={PlusIcon}
          />
        </Card>
        <div className={"mt-4"}>
          <Card>
            <div className={"mb-1 flex items-center justify-between gap-3"}>
              <span className={"text-sm font-medium text-slate-500"}>
                {visibleTopics.length}{" "}
                {visibleTopics.length === 1 ? "topic" : "topics"}
                {visibleTopics.length < topics.length
                  ? ` of ${topics.length}`
                  : ""}
              </span>
              <SortedByNotice sort={sort} onReset={() => setSort(undefined)} />
            </div>
            <div className={"mb-4"}>
              <DebouncedTextInput
                initialValue={filters.search}
                debounceInMs={300}
                onChange={setSearch}
                placeholder={"Search by name, level, PDF url, user group or ID"}
              />
            </div>
            {loading ? (
              <LoadingRows rows={8} />
            ) : (
              <TopicTable
                topics={visibleTopics}
                userGroupOptions={userGroupOptions}
                sort={sort}
                onSortChange={setSort}
                filters={filters}
                onFiltersChange={setColumnFilters}
                deleteTopic={deleteTopic}
                editTopic={(topic) => toEditTopic(topic.id)}
              />
            )}
          </Card>
        </div>
      </Layout>
    </>
  );
}
